import type { FC } from 'react'
import { useEffect } from 'react'
import { MemberHashKey } from '../../../consts/memberHashKey'
import { OmniaComponents, Services } from '../../../services'
import { getLogger } from '../../../services/logger/logger.service'
import { cancelOrder } from '../../../utils/cancelOrder'
import { getLocalStorage, setLocalStorage } from '../../../utils/localStorage'

const localStorageKey = 'root-error-boundary-did-refresh'

// Additional logger tags to allow filtering in site 24x7 of first time (quick refresh) vs manual refresh instances
const loggerQuickRefresh = getLogger(['RootLevelErrorBoundary', 'QuickRefresh'])
const loggerManualRefresh = getLogger(['RootLevelErrorBoundary', 'ManualRefresh'])

export const RootLevelErrorBoundary: FC = () => {
    const { title, description, refreshButtonText } = Services.CmsDataService.get().rootErrorConfiguration

    const isFirstError = !getLocalStorage(localStorageKey)

    useEffect(() => {
        if (!isFirstError) {
            loggerManualRefresh.value.error('Full screen error triggered and displayed.')
            return
        }

        loggerQuickRefresh.value.warn('Full screen error trigger for the first time, attempting refresh.')
        setLocalStorage(localStorageKey, true)
        void refresh()
    }, [])

    const refresh = async (clearCache: boolean = false) => {
        if (!clearCache) {
            location.reload()
            return
        }

        // Release any held seats
        try {
            await cancelOrder()
        } catch (ex) {
            loggerManualRefresh.value.error('Error cancelling order', ex)
        }

        // Keep member hash in localStorage
        const vdMemHash = localStorage.getItem(MemberHashKey)

        localStorage.clear()
        sessionStorage.clear()

        if (vdMemHash) {
            localStorage.setItem(MemberHashKey, vdMemHash)
        }

        loggerManualRefresh.value.debug('Local and session storage cleared')

        // Refresh page with clearCache param, triggers backend to add cache and cookie clearing headers
        // Can not reliably clear cookies via javascript as some cookies are inaccessible by javascript with HttpOnly
        const url = new URL(location.href)
        url.searchParams.set('clearCache', '')

        loggerManualRefresh.value.warn('Attempting to refresh and clear cache from full screen error state.')
        window.location.href = url.href
    }

    if (isFirstError) return null

    if (OmniaComponents.ErrorBoundary) {
        return <OmniaComponents.ErrorBoundary />
    }

    return (
        <div
            style={{
                margin: 'auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px',
            }}
        >
            <h1>{title}</h1>
            <h3>{description}</h3>
            <button onClick={() => void refresh(true)}>{refreshButtonText}</button>
        </div>
    )
}
